import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterOptions } from '@hero/components/molecules/Table/Filter/context';
import { AVAILABLE_OCCASION_NAMES } from '@hero/constants';
import { useIsAdminOrganization, useAutomationTableFilterOptions } from '@hero/hooks';
import { TableSelectFilter } from '@hero/molecules';
import { useGetOrganizationUsers } from '@hero/services/organization-service/hooks';
import { getName } from '@hero/utils';

import * as S from './style';

function Filter({ globalFilters, placeholder, setGlobalFilters }) {
  const { t } = useTranslation();
  const automationFilterOptions = useAutomationTableFilterOptions();
  const isOrganizationAdmin = useIsAdminOrganization();

  const placeholderText = useMemo(() => placeholder, [placeholder]);

  const { data: organizationUsers } = useGetOrganizationUsers(
    {
      filters: {
        statuses: ['active']
      }
    },
    {
      enabled: !!isOrganizationAdmin
    }
  );

  return (
    <S.FilterWrapper>
      {t('filterBy')}:
      <FilterOptions.Provider
        value={{
          inputPlaceholderOnMenu: placeholderText,
          hideSearchOnMenu: true
        }}
      >
        <TableSelectFilter
          isMulti
          creatable={false}
          options={automationFilterOptions}
          onChange={filters => {
            setGlobalFilters(passValues => ({
              ...passValues,
              filters: {
                ...passValues.filters,
                status: filters
              }
            }));
          }}
          selectedOption={globalFilters?.filters?.status}
          placeholder={t('status')}
          hasMenuSearch={false}
        />

        <TableSelectFilter
          isMulti
          creatable={false}
          options={Object.entries(AVAILABLE_OCCASION_NAMES).map((key, value) => ({
            label: t(key),
            value: value
          }))}
          onChange={filters => {
            setGlobalFilters(passValues => ({
              ...passValues,
              filters: {
                ...passValues.filters,
                type: filters
              }
            }));
          }}
          selectedOption={globalFilters?.filters?.type}
          placeholder={t('automationType')}
          hasMenuSearch={false}
        />
        {isOrganizationAdmin && (
          <TableSelectFilter
            creatable={false}
            isMulti
            options={organizationUsers?.docs?.map(user => ({
              label: getName({ firstName: user.firstName, lastName: user.lastName }),
              value: user.email
            }))}
            onChange={filters => {
              setGlobalFilters(passValues => ({
                ...passValues,
                filters: {
                  ...passValues.filters,
                  createdBy: filters
                }
              }));
            }}
            selectedOption={globalFilters?.filters?.createdBy}
            placeholder={t('createdBy')}
            searchPlaceholder={`${t('searchCreators')}...`}
          />
        )}
      </FilterOptions.Provider>
    </S.FilterWrapper>
  );
}

Filter.propTypes = {
  globalFilters: PropTypes.shape({
    filters: PropTypes.shape({
      status: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
      type: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
      createdBy: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
    })
  }),
  placeholder: PropTypes.string,
  setGlobalFilters: PropTypes.func
};

export { Filter };
